import { Link, useLocation } from 'react-router-dom'

import { LANG_STORAGE_KEY, counterpartPath, type Lang } from '../i18n'
import styles from './LangToggle.module.css'

const LABELS: Record<Lang, string> = { sv: 'Svenska', en: 'English' }

/**
 * Swedish and English are separate, prerendered URLs, so switching language is a
 * navigation and this is a link, not a button over some state.
 *
 * Nothing redirects a first-time visitor to "their" language. A page that serves
 * different content at the same URL depending on who is asking is the thing hreflang
 * exists to avoid, and it would mean the prerendered HTML a crawler reads is not what a
 * person sees. The choice is still remembered, which is what the stored value is for:
 * it costs nothing and a later visit can honour it if this site ever grows a reason to.
 */
export default function LangToggle({ current }: { current: Lang }) {
  const { pathname } = useLocation()

  const remember = (lang: Lang) => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang)
    } catch {
      // Private-browsing modes throw on write. Remembering is a nicety, not the feature.
    }
  }

  return (
    <span className={styles.toggle}>
      {(['sv', 'en'] as Lang[]).map((lang, index) => (
        <span key={lang} className={styles.item}>
          {index > 0 && <span className={styles.divider}>/</span>}
          {lang === current ? (
            <span className={styles.active} aria-current="true" lang={lang}>
              {LABELS[lang]}
            </span>
          ) : (
            <Link
              to={counterpartPath(pathname, lang)}
              className={styles.link}
              lang={lang}
              hrefLang={lang}
              onClick={() => remember(lang)}
            >
              {LABELS[lang]}
            </Link>
          )}
        </span>
      ))}
    </span>
  )
}
